import { Button } from '@limitless-minds/ui-button'
import { ArrowBack } from '@mui/icons-material'
import { alpha, Box, Stack, styled } from '@mui/material'

import Stepper from '@/ui/molecules/Stepper'

const StyledBackground = styled(Stack)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  height: '100%',
  background: `url('/images/halftone-background.png') repeat`,
  backgroundSize: '30rem',

  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    // The veil is the surface, so it has to come from a role. At 0.9 the literal does not tint the
    // halftone underneath - it REPLACES it, so in dark it would stay a near-white field while the ink on
    // top follows `onSurface`: white on white, which is the 1.01 this feature already paid for once.
    //
    // The PNG is opaque and greyscale, a white field at 255 with dots at 0. Composed over the field,
    // `alpha(surface, 0.9)` gives #222B36 in dark and #FAFBFD in light, with the ink on it at 14.32 and
    // 13.42. The texture survives and keeps its polarity - the dots read 1.31 against the field in dark
    // and 1.25 in light - so the image needs no dark variant.
    //
    // That composite is what `roles.ts` names `surfaceVeiled`, and every ink that lands here is declared
    // against it. The role resolves to the nearest primitive on the strict side, so the matrix measures a
    // hair below what the member sees rather than above.
    //
    // What is NOT symmetric is the elevation: the residual tenth of a near-white texture lifts the dark
    // composite 24 levels off the canvas and the light one by 1, so in dark this column reads as a raised
    // surface and in light it reads flush with the page. That is a step that exists only in dark, and it
    // is left for design rather than hidden by raising the alpha, which would erase the texture.
    backgroundColor: alpha(theme.palette.role.surface, 0.9),
    zIndex: 0,
  },

  '& > *': {
    position: 'relative',
    zIndex: 1,
  },

  [theme.breakpoints.down('lg')]: {
    background: 'none',
    '&::after': {
      display: 'none',
    },
  },
}))

const StyledBackButton = styled(Button)(({ theme }) => ({
  alignSelf: 'center',
  '&:hover': {
    backgroundColor: 'transparent',
  },
  [theme.breakpoints.up('lg')]: {
    alignSelf: 'flex-start',
  },
}))
interface BodyProps {
  steps: React.JSX.Element[]
  handleBackStep: () => void
  activeStep: number
  hideBackButton?: boolean
}

const Body: React.FC<BodyProps> = ({ steps, handleBackStep, activeStep, hideBackButton }) => {
  return (
    <StyledBackground>
      {!hideBackButton && (
        <Box width='100%' display={activeStep > 0 ? 'flex' : 'none'}>
          <StyledBackButton onClick={handleBackStep} design='text' startIcon={<ArrowBack />}>
            Go Back
          </StyledBackButton>
        </Box>
      )}
      <Box
        sx={{
          padding: { md: '4rem' },
          display: 'flex',
          width: '100%',
          height: '100%',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Stepper currentActiveStep={activeStep} steps={steps} />
      </Box>
    </StyledBackground>
  )
}

export default Body
